import React from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { SearchInput } from '../../components/common/SearchInput';
import { StatusSelector } from '../../components/common/StatusSelector';
import { updateArtifact } from '../../api/services/artifacts';
import { SensitiveDataTable } from '../../components/Knowledge/SensitiveDataTable';
import {
  useGraphStats,
  useKnowledgeSearch,
  useKnowledgeSources,
  useUploadFiles,
} from '../../api/hooks/knowledge.hook';
import type { KnowledgeSource, SearchHit } from '../../api/services/knowledge';
import { getDocumentContent, getDocumentStatus } from '../../api/services/knowledge';
import KnowledgeGraphOverview from '../../components/Knowledge/KnowledgeGraphOverview';
import { useWorkspace } from '../../components/Workspace/artifacts';
import type { Artifact } from '../../components/Workspace/artifacts';
import type { ArtifactStatus } from '../../api/services/artifacts';
import {
  listArtifactVersions,
  revertArtifactVersion,
  type ArtifactVersion,
} from '../../api/services/artifacts';

type UploadStatus = 'pending' | 'uploading' | 'indexing' | 'complete' | 'error';

type UploadItem = {
  id: string;
  file: File;
  name: string;
  size: number;
  status: UploadStatus;
  progress: number;
  sourceId?: string;
  documentId?: string;
};

type SourceDescriptor = {
  type: 'Document' | 'Connector' | 'Email';
  label: string;
  detail?: string;
};

const formatBytes = (value: number): string => {
  if (!value) {
    return '—';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const order = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  const scaled = value / 1024 ** order;
  return `${scaled >= 10 ? scaled.toFixed(0) : scaled.toFixed(1)} ${units[order]}`;
};

const formatRelativeTime = (iso?: string | null): string => {
  if (!iso) {
    return '—';
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 60_000) {
    return 'moments ago';
  }
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 14) {
    return `${days}d ago`;
  }
  const weeks = Math.round(days / 7);
  if (weeks < 8) {
    return `${weeks}w ago`;
  }
  const months = Math.round(days / 30);
  if (months < 18) {
    return `${months}mo ago`;
  }
  const years = Math.round(days / 365);
  return `${years}y ago`;
};

const describeSearchSource = (source: string, artifacts?: Artifact[]): SourceDescriptor => {
  if (source.startsWith('connector:')) {
    const [, provider, ...rest] = source.split(':');
    const resource = rest.join(':');
    return {
      type: 'Connector',
      label: resource || provider,
      detail: provider.replace(/_/g, ' '),
    };
  }
  if (source.startsWith('emails:')) {
    const [, thread] = source.split(':', 2);
    return {
      type: 'Email',
      label: thread || 'Email thread',
      detail: 'Workspace emails',
    };
  }
  // Check if source is an artifact ID
  if (source.startsWith('artifact:') && artifacts) {
    const normalizeArtifactId = (raw: string) =>
      raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');
    const normalizedId = normalizeArtifactId(source);
    const artifact = artifacts.find((a) => {
      const normalizedArtifactId = normalizeArtifactId(a.id);
      return normalizedArtifactId === normalizedId || a.id === source;
    });
    if (artifact) {
      return {
        type: 'Document',
        label: artifact.title,
        detail: 'Workspace document',
      };
    }
  }
  return {
    type: 'Document',
    label: source,
    detail: 'Workspace document',
  };
};

const statusBadgeClasses: Record<UploadStatus | KnowledgeSource['status'], string> = {
  pending: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  uploading: 'bg-blue-50 text-blue-700 border-blue-200',
  indexing: 'bg-amber-50 text-amber-700 border-amber-200',
  complete: 'bg-green-50 text-green-700 border-green-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  ready: 'bg-green-50 text-green-700 border-green-200',
};

const statusLabel: Record<UploadStatus | KnowledgeSource['status'], string> = {
  pending: 'Queued',
  uploading: 'Uploading',
  indexing: 'Indexing',
  complete: 'Ready',
  error: 'Failed',
  ready: 'Ready',
};

const artifactTypeLabels: Record<string, string> = {
  document: 'Document',
  email: 'Email Draft',
  report: 'Report',
  data: 'Data',
};

const KnowledgePage: React.FC = () => {
  const navigate = useNavigate();
  const uploadMutation = useUploadFiles();
  const progressTimers = React.useRef<Record<string, number>>({});
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadItems, setUploadItems] = React.useState<UploadItem[]>([]);
  const [searchResults, setSearchResults] = React.useState<SearchHit[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [searchInputKey, setSearchInputKey] = React.useState(0);
  const [expandedVersions, setExpandedVersions] = React.useState<string | null>(null);
  const [versions, setVersions] = React.useState<Record<string, ArtifactVersion[]>>({});
  const [versionLoading, setVersionLoading] = React.useState<Record<string, boolean>>({});
  const [expandedCards, setExpandedCards] = React.useState<Set<string>>(new Set());
  const [openingDocuments, setOpeningDocuments] = React.useState<Set<string>>(new Set());

  const knowledgeSourcesQuery = useKnowledgeSources();
  const graphStatsQuery = useGraphStats();
  const searchMutation = useKnowledgeSearch();
  const graphStats = graphStatsQuery.data;

  // Get workspace artifacts
  const {
    state: workspaceState,
    openArtifact,
    deleteArtifact,
    refreshArtifacts,
    createDocument,
  } = useWorkspace();

  const handleStatusChange = async (artifactId: string, newStatus: ArtifactStatus) => {
    try {
      await updateArtifact(artifactId, { status: newStatus });
      await refreshArtifacts();
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  React.useEffect(() => {
    return () => {
      Object.values(progressTimers.current).forEach((timer) => {
        window.clearInterval(timer);
      });
      progressTimers.current = {};
    };
  }, []);

  React.useEffect(() => {
    const active = uploadItems.filter((item) => item.status === 'indexing' && item.documentId);
    if (active.length === 0) {
      return;
    }

    let cancelled = false;
    const timer = window.setInterval(async () => {
      if (cancelled) {
        return;
      }
      await Promise.all(
        active.map(async (item) => {
          if (!item.documentId) {
            return;
          }
          try {
            const status = await getDocumentStatus(item.documentId);
            setUploadItems((prev) =>
              prev.map((entry) => {
                if (entry.id !== item.id) {
                  return entry;
                }
                const backendStatus = String(status.status ?? '').toUpperCase();
                if (backendStatus === 'READY' || backendStatus === 'COMPLETE') {
                  void refreshArtifacts();
                  return { ...entry, status: 'complete', progress: 100 };
                }
                if (backendStatus === 'FAILED') {
                  return { ...entry, status: 'error', progress: 0 };
                }
                return entry;
              }),
            );
          } catch (error) {
            // best-effort polling; fallback source refresh effect still applies
          }
        }),
      );
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [uploadItems]);

  React.useEffect(() => {
    const handleCommandUpload = () => {
      fileInputRef.current?.click();
    };
    document.addEventListener('app:upload', handleCommandUpload as EventListener);
    return () => {
      document.removeEventListener('app:upload', handleCommandUpload as EventListener);
    };
  }, []);

  const startProgress = React.useCallback((id: string) => {
    progressTimers.current[id] = window.setInterval(() => {
      setUploadItems((prev) =>
        prev.map((item) => {
          if (item.id !== id || item.status !== 'uploading') {
            return item;
          }
          const increment = Math.random() * 12 + 4;
          const nextProgress = Math.min(item.progress + increment, 90);
          if (nextProgress <= item.progress) {
            return item;
          }
          return { ...item, progress: nextProgress };
        }),
      );
    }, 400);
  }, []);

  const stopProgress = React.useCallback((id: string) => {
    const timer = progressTimers.current[id];
    if (timer) {
      window.clearInterval(timer);
      delete progressTimers.current[id];
    }
  }, []);

  const uploadParallel = React.useCallback(
    async (items: UploadItem[]) => {
      await Promise.all(
        items.map(async (item) => {
          setUploadItems((prev) =>
            prev.map((entry) =>
              entry.id === item.id
                ? {
                    ...entry,
                    status: 'uploading' as UploadStatus,
                    progress: Math.max(entry.progress, 5),
                  }
                : entry,
            ),
          );
          startProgress(item.id);
          try {
            const response = await uploadMutation.mutateAsync([item.file]);
            const upload = response.uploads?.[0];
            const documentId = upload?.document_id ?? response.ids[0] ?? item.id;
            const backendStatus = String(upload?.status ?? 'UPLOADED').toUpperCase();
            stopProgress(item.id);
            setUploadItems((prev) =>
              prev.map((entry) =>
                entry.id === item.id
                  ? {
                      ...entry,
                      status:
                        backendStatus === 'FAILED'
                          ? ('error' as UploadStatus)
                          : ('indexing' as UploadStatus),
                      progress: backendStatus === 'FAILED' ? 0 : 100,
                      sourceId: upload?.filename ?? item.name,
                      documentId,
                    }
                  : entry,
              ),
            );
            toast.success(`Uploaded ${item.name}`);
            void refreshArtifacts();
          } catch (error) {
            stopProgress(item.id);
            setUploadItems((prev) =>
              prev.map((entry) =>
                entry.id === item.id
                  ? { ...entry, status: 'error' as UploadStatus, progress: 0 }
                  : entry,
              ),
            );
            toast.error(`Failed to upload ${item.name}`);
          }
        }),
      );
    },
    [startProgress, stopProgress, uploadMutation, refreshArtifacts],
  );

  const enqueueFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      const prepared = files.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name,
        size: file.size,
        status: 'pending' as UploadStatus,
        progress: 0,
      }));
      setUploadItems((prev) => [...prev, ...prepared]);
      void uploadParallel(prepared);
    },
    [uploadParallel],
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files || []);
    enqueueFiles(files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    enqueueFiles(files);
    event.target.value = '';
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setSearchError(null);
    if (!query) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await searchMutation.mutateAsync(query);
      setSearchResults(response.hits);
      if (response.hits.length === 0) {
        toast('No results matched your query.');
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Search request failed. Please try again.';
      setSearchError(message);
      toast.error(message);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSearchInputKey((value) => value + 1);
  };

  const handleOpenDocument = async (hit: SearchHit) => {
    const descriptor = describeSearchSource(hit.source, workspaceState.artifacts);

    // Check if hit.source is an artifact ID (starts with "artifact:")
    if (hit.source.startsWith('artifact:')) {
      const normalizedId = normalizeArtifactId(hit.source);

      // First check if it's already in the artifacts list
      let matchingArtifact = workspaceState.artifacts.find((artifact) => {
        const normalizedArtifactId = normalizeArtifactId(artifact.id);
        return normalizedArtifactId === normalizedId || artifact.id === hit.source;
      });

      // If not found, refresh artifacts and try again
      if (!matchingArtifact) {
        await refreshArtifacts();
        matchingArtifact = workspaceState.artifacts.find((artifact) => {
          const normalizedArtifactId = normalizeArtifactId(artifact.id);
          return normalizedArtifactId === normalizedId || artifact.id === hit.source;
        });
      }

      if (matchingArtifact) {
        const id = normalizeArtifactId(matchingArtifact.id);
        openArtifact(id);
        navigate(`/?artifact=${id}`);
      } else {
        // Try opening directly with the normalized ID (might work if backend loads it)
        openArtifact(normalizedId);
        navigate(`/?artifact=${normalizedId}`);
      }
      return;
    }

    // Skip connectors - they can't be opened as documents
    if (hit.source.startsWith('connector:')) {
      toast.error('Connector sources cannot be opened as documents');
      return;
    }

    // For indexed documents (not artifacts), try to find existing artifact:
    // 1. By sourceFilename (auto-created from upload)
    // 2. By title match
    let matchingArtifact = workspaceState.artifacts.find((artifact) => {
      if (artifact.type === 'document' && artifact.sourceFilename) {
        return artifact.sourceFilename === hit.source;
      }
      return artifact.title === descriptor.label || artifact.title === hit.source;
    });

    if (matchingArtifact) {
      // Found existing artifact, open it
      const id = normalizeArtifactId(matchingArtifact.id);
      openArtifact(id);
      navigate(`/?artifact=${id}`);
      return;
    }

    // Not found locally — refresh from backend and try again
    await refreshArtifacts();
    matchingArtifact = workspaceState.artifacts.find((artifact) => {
      if (artifact.type === 'document' && artifact.sourceFilename) {
        return artifact.sourceFilename === hit.source;
      }
      return artifact.title === descriptor.label || artifact.title === hit.source;
    });

    if (matchingArtifact) {
      const id = normalizeArtifactId(matchingArtifact.id);
      openArtifact(id);
      navigate(`/?artifact=${id}`);
      return;
    }

    // Still not found - fetch content and create a new artifact from the indexed document
    setOpeningDocuments((prev) => new Set(prev).add(hit.source));
    try {
      console.log('Fetching document content for:', hit.source);
      const contentResponse = await getDocumentContent(hit.source);

      if (!contentResponse || !contentResponse.content) {
        throw new Error('Document content is empty');
      }

      console.log('Creating artifact from document:', descriptor.label);
      const newArtifact = await createDocument(descriptor.label, contentResponse.content, 'user');
      const id = normalizeArtifactId(newArtifact.id);
      openArtifact(id);
      navigate(`/?artifact=${id}`);
      toast.success(`Opened "${descriptor.label}" in workspace`);
    } catch (error) {
      console.error('Failed to fetch/open document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to open document "${descriptor.label}": ${errorMessage}`);
    } finally {
      setOpeningDocuments((prev) => {
        const next = new Set(prev);
        next.delete(hit.source);
        return next;
      });
    }
  };

  const normalizeArtifactId = (raw: string) =>
    raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');

  const loadVersions = async (artifactId: string) => {
    setVersionLoading((prev) => ({ ...prev, [artifactId]: true }));
    try {
      const data = await listArtifactVersions(artifactId);
      setVersions((prev) => ({ ...prev, [artifactId]: data }));
    } catch (error) {
      console.error('Failed to load versions', error);
    } finally {
      setVersionLoading((prev) => ({ ...prev, [artifactId]: false }));
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-neutral-100">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-4 sm:py-6 lg:px-8">
        <header className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">Knowledge</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Upload documents, connect sources, and search your workspace.
          </p>
        </header>

        <section className="mb-4 grid grid-cols-2 gap-3 sm:mb-6 sm:grid-cols-4">
          <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">Graph Nodes</p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">
              {graphStatsQuery.isLoading ? '...' : (graphStats?.total_nodes ?? 0)}
            </p>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">Graph Edges</p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">
              {graphStatsQuery.isLoading ? '...' : (graphStats?.total_relationships ?? 0)}
            </p>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">Indexed Docs</p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">
              {graphStatsQuery.isLoading ? '...' : (graphStats?.documents_total ?? 0)}
            </p>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">Indexed Chunks</p>
            <p className="mt-1 text-lg font-semibold text-neutral-900">
              {graphStatsQuery.isLoading ? '...' : (graphStats?.chunks_total ?? 0)}
            </p>
          </div>
        </section>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <section className="rounded-2xl sm:rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm">
            <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
              Upload documents
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-neutral-500">
              Upload files into your workspace. They will be parsed and indexed for search.
            </p>
            <div
              className={`mt-4 flex flex-col items-center justify-center gap-3 rounded-xl sm:rounded-2xl border-2 border-dashed px-4 sm:px-6 py-8 sm:py-12 text-center transition ${
                isDragging
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-neutral-200 bg-neutral-50 text-neutral-600'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-sm font-medium flex flex-col sm:flex-row items-center gap-2">
                <span className="hidden sm:inline">Drag & drop files here or</span>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full border border-blue-200 bg-blue-500 px-4 py-2 sm:px-3 sm:py-1 text-sm sm:text-xs font-semibold text-white transition hover:bg-blue-600 active:scale-95"
                >
                  Browse files
                </button>
              </div>
              <p className="text-xs text-neutral-500">PDF, CSV, Excel, Markdown, and more.</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                accept=".pdf,.csv,.txt,.md,.doc,.docx,.xls,.xlsx"
              />
            </div>

            {uploadItems.length > 0 ? (
              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-semibold text-neutral-800">Recent uploads</h3>
                <ul className="space-y-3">
                  {uploadItems.map((item) => (
                    <li
                      key={item.id}
                      className="rounded-2xl border border-neutral-200 px-4 py-3 shadow-sm"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-neutral-900">{item.name}</p>
                          <p className="text-xs text-neutral-500">{formatBytes(item.size)}</p>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusBadgeClasses[item.status]}`}
                        >
                          {statusLabel[item.status]}
                        </span>
                      </div>
                      <div className="mt-3 h-2 w-full rounded-full bg-neutral-100">
                        <div
                          className={`h-2 rounded-full ${
                            item.status === 'error'
                              ? 'bg-red-400'
                              : item.status === 'complete'
                                ? 'bg-green-500'
                                : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.round(item.progress)}%` }}
                        />
                      </div>
                      {item.status === 'error' ? (
                        <p className="mt-2 text-xs text-red-600">
                          Something went wrong while uploading this file. Please try again.
                        </p>
                      ) : null}
                      {item.status === 'indexing' ? (
                        <p className="mt-2 text-xs text-amber-600">
                          File uploaded. Finishing indexing…
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <section className="rounded-2xl sm:rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm">
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
                  Search knowledge
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-neutral-500">
                  Find answers across your uploaded documents and sources.
                </p>
              </div>
              {searchQuery ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-xs font-medium text-blue-600 transition hover:text-blue-700 shrink-0"
                >
                  Clear
                </button>
              ) : null}
            </div>

            <div className="mt-4">
              <SearchInput
                key={searchInputKey}
                placeholder="Search knowledge…"
                onSearch={handleSearch}
              />
            </div>

            <div className="mt-4 space-y-4">
              {searchMutation.isPending ? (
                <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                  <span className="h-2 w-2 animate-ping rounded-full bg-blue-400" />
                  Searching workspace…
                </div>
              ) : null}

              {searchError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {searchError}
                </div>
              ) : null}

              {!searchMutation.isPending &&
              !searchError &&
              searchQuery &&
              searchResults.length === 0 ? (
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
                  No results found for “{searchQuery}”. Try a different keyword.
                </div>
              ) : null}

              {searchResults.map((hit) => {
                const descriptor = describeSearchSource(hit.source, workspaceState.artifacts);
                return (
                  <article
                    key={hit.id}
                    onClick={() => !openingDocuments.has(hit.source) && handleOpenDocument(hit)}
                    className={`rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm transition ${
                      openingDocuments.has(hit.source)
                        ? 'opacity-50 cursor-wait'
                        : 'cursor-pointer hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex shrink-0 items-center rounded-full border border-neutral-200 bg-neutral-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
                          {descriptor.type}
                        </span>
                      </div>
                      <p className="truncate text-sm font-semibold text-neutral-900">
                        {descriptor.label}
                      </p>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-neutral-700">
                      {hit.snippet}
                    </p>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        {/* Workspace Artifacts Section */}
        {workspaceState.artifacts.length > 0 && (
          <section className="mt-4 sm:mt-6 rounded-2xl sm:rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm">
            <div className="sticky top-0 z-10 bg-white rounded-t-2xl sm:rounded-t-3xl -mx-4 -mt-4 px-4 pt-4 pb-2 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6 flex items-start sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
                  Your Documents
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-neutral-500">
                  Documents you have created and uploaded.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-xs font-medium text-blue-600 transition hover:text-blue-700 shrink-0"
              >
                Open Workspace
              </button>
            </div>

            <div className="mt-4 divide-y divide-neutral-200">
              {workspaceState.artifacts.map((artifact) => {
                const isExpanded = expandedCards.has(artifact.id);
                const toggleCard = () => {
                  setExpandedCards((prev) => {
                    const next = new Set(prev);
                    if (next.has(artifact.id)) {
                      next.delete(artifact.id);
                    } else {
                      next.add(artifact.id);
                    }
                    return next;
                  });
                };

                const handleArtifactClick = (e: React.MouseEvent) => {
                  // Don't open if clicking on buttons or expand toggle
                  const target = e.target as HTMLElement;
                  if (target.closest('button') || target.closest('[role="button"]')) {
                    return;
                  }
                  const id = normalizeArtifactId(artifact.id);
                  openArtifact(id);
                  navigate(`/?artifact=${id}`);
                };

                return (
                  <div
                    key={artifact.id}
                    onClick={handleArtifactClick}
                    className="py-3 sm:py-4 text-sm text-neutral-700 cursor-pointer hover:bg-blue-50 transition-colors duration-200"
                  >
                    {/* Mobile: Stack vertically */}
                    <div className="flex flex-col gap-3 sm:hidden">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCard();
                            }}
                            className="text-left w-full"
                          >
                            <p
                              className={`font-medium text-neutral-900 ${isExpanded ? '' : 'truncate'}`}
                            >
                              {artifact.title}
                            </p>
                          </button>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {artifact.createdBy === 'agent' ? 'Assistant' : 'You'} •{' '}
                            {formatRelativeTime(artifact.updatedAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                            {artifactTypeLabels[artifact.type] || artifact.type}
                          </span>
                          <StatusSelector
                            currentStatus={artifact.status}
                            onStatusChange={async (status) => {
                              await handleStatusChange(artifact.id, status);
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = expandedVersions === artifact.id ? null : artifact.id;
                            setExpandedVersions(next);
                            if (next) {
                              void loadVersions(artifact.id);
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-lg border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-100 active:scale-95"
                        >
                          Versions
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${artifact.title}"?`)) {
                              deleteArtifact(artifact.id);
                              toast.success('Document deleted');
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 transition hover:bg-red-50 hover:text-red-700 active:scale-95 ml-auto"
                          title="Delete"
                          aria-label="Delete document"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Desktop: Grid layout */}
                    <div className="hidden sm:grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,120px)_minmax(0,200px)] items-center">
                      <div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCard();
                          }}
                          className="text-left w-full"
                        >
                          <p
                            className={`font-medium text-neutral-900 ${isExpanded ? '' : 'truncate'}`}
                          >
                            {artifact.title}
                          </p>
                        </button>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          Created by {artifact.createdBy === 'agent' ? 'Assistant' : 'You'} •{' '}
                          {formatRelativeTime(artifact.updatedAt)}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
                          {artifactTypeLabels[artifact.type] || artifact.type}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <StatusSelector
                          currentStatus={artifact.status}
                          onStatusChange={async (status) => {
                            await handleStatusChange(artifact.id, status);
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = expandedVersions === artifact.id ? null : artifact.id;
                            setExpandedVersions(next);
                            if (next) {
                              void loadVersions(artifact.id);
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-100"
                        >
                          Versions
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${artifact.title}"?`)) {
                              deleteArtifact(artifact.id);
                              toast.success('Document deleted');
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-full p-2 text-red-600 transition hover:bg-red-50 hover:text-red-700"
                          title="Delete"
                          aria-label="Delete document"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {expandedVersions === artifact.id && (
                      <div className="mt-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
                        {versionLoading[artifact.id] ? (
                          <div className="text-xs text-neutral-500">Loading versions…</div>
                        ) : versions[artifact.id] && versions[artifact.id].length > 0 ? (
                          versions[artifact.id].map((v) => (
                            <div
                              key={v.id}
                              className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium text-neutral-900">
                                  {new Date(v.created_at).toLocaleString()}
                                </span>
                                <span className="text-xs text-neutral-500">{v.title}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-600">
                                  {v.status}
                                </span>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await revertArtifactVersion(artifact.id, v.id);
                                    await refreshArtifacts();
                                    openArtifact(artifact.id);
                                    toast.success('Version restored');
                                  }}
                                  className="inline-flex items-center justify-center rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-blue-600"
                                >
                                  Restore
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-neutral-500">No versions yet.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="mt-4 sm:mt-6 rounded-2xl sm:rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
                Knowledge Graph
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-neutral-500">
                All entities and relationships extracted across your indexed documents.
              </p>
            </div>
          </div>
          <KnowledgeGraphOverview />
        </section>

        <section className="mt-4 sm:mt-6 rounded-2xl sm:rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 shadow-sm">
          <div className="sticky top-0 z-10 bg-white rounded-t-2xl sm:rounded-t-3xl -mx-4 -mt-4 px-4 pt-4 pb-2 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6 flex items-start sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
                Sensitive Data
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-neutral-500">
                View all sensitive data that has been detected and coded in your documents.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <SensitiveDataTable />
          </div>
        </section>

        {/* Indexed sources section removed – all artifacts shown via "Your Documents" above - uploads are being transformed to artifacts
            TO DO could change to also show tables / tab data)*/}
      </div>
    </div>
  );
};

export default KnowledgePage;
